import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { requestPasswordReset } from '../lib/api';
import { Shell } from './Welcome';

/**
 * "Forgot password?" from the sign-in page. Emails a one-time link to
 * /welcome?type=recovery, where the person chooses a new password. The
 * confirmation reads the same whether or not the address has an account.
 */
export function ForgotPassword(): JSX.Element {
  const { user } = useAuth();
  const loc = useLocation();
  const initialEmail = (loc.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (user) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) return setErr('Enter your work email address.');
    setErr(null);
    setSending(true);
    try {
      await requestPasswordReset(address);
      setSentTo(address);
    } catch (error) {
      setErr((error as Error).message);
    } finally {
      setSending(false);
    }
  }

  if (sentTo) {
    return (
      <Shell title="Check your email">
        <div className="login-sub">
          If {sentTo} has an account, we have emailed it a link to choose a new password. The link works once and
          expires after a while. Nothing there? Check spam, or ask an admin to help.
        </div>
        <Link className="btn btn-secondary" to="/login">Back to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell title="Reset your password">
      <div className="login-sub">Enter your work email and we will send you a link to choose a new password.</div>
      <form className="login-form" onSubmit={submit}>
        <div>
          <label className="input-label" htmlFor="forgot-email">Work email</label>
          <input
            id="forgot-email"
            className="input"
            style={{ width: '100%' }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />
          {err && <div className="input-error">{err}</div>}
        </div>
        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? <><span className="spinner" />Sending…</> : 'Send reset link'}
        </button>
      </form>
      <div className="login-foot">
        <Link className="btn-link" to="/login">Back to sign in</Link>
      </div>
    </Shell>
  );
}
